import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { cartPayload, normalizePhone } from "@/lib/cartStorage";
import { acknowledgeCart, reconcileCart } from "@/lib/cartReconcile";

export function useCartSync({ phone, cart, cartRef, ensureCartSession, save }) {
  const [status, setStatus] = useState(phone ? "restoring" : "saved");
  const [ready, setReady] = useState(!phone);
  const token = useRef("");
  const alive = useRef(true);
  const restored = useRef(false);
  const running = useRef(null);
  const queued = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const sync = useCallback(() => {
    if (!phone) return Promise.resolve(true);
    if (running.current) { queued.current = true; return running.current; }
    running.current = (async () => {
      let success = false;
      try {
        const session = await ensureCartSession();
        if (!alive.current || normalizePhone(session?.phone) !== phone) return false;
        token.current = session.cart_token;
        const request = async (method, path, data) => {
          const send = () => api.request({ method, url: path, data, timeout: 15000, headers: { Authorization: `Bearer ${token.current}` } });
          try { return await send(); }
          catch (error) {
            if (error.response?.status !== 401) throw error;
            const fresh = await ensureCartSession(true);
            if (!alive.current || normalizePhone(fresh?.phone) !== phone) throw error;
            token.current = fresh.cart_token;
            return send();
          }
        };
        let conflicts = 0;
        // Drain genuine edits until settled; a burst of quantity taps must not
        // be mistaken for a network failure just because it needed six saves.
        while (alive.current) {
          queued.current = false;
          setStatus(restored.current ? "saving" : "restoring");
          const { data } = await request("get", "/cart/current");
          if (!alive.current) return false;
          const reconciled = reconcileCart(cartRef.current, data.cart);
          save(reconciled);
          restored.current = true;
          setReady(true);
          if (!reconciled.dirty) {
            setStatus("saved");
            success = true;
            if (!queued.current) break;
            continue;
          }
          try {
            save({ ...cartRef.current, attempt: { changeId: reconciled.changeId, items: reconciled.items } });
            const result = (await request("post", "/cart/sync", cartPayload(reconciled))).data;
            if (!alive.current) return false;
            conflicts = 0;
            if (result.accepted && result.state !== "submitted") {
              save(acknowledgeCart(cartRef.current, reconciled, result));
            }
            // Read again after acknowledgement/conflict, including any changes from another device.
            queued.current = true;
          } catch (error) {
            if (error.response?.status !== 409) throw error;
            conflicts += 1;
            if (conflicts >= 6) throw error;
            await new Promise((resolve) => setTimeout(resolve, Math.min(100 * 2 ** conflicts, 2000)));
            queued.current = true;
          }
        }
      } catch (error) {
        if (alive.current) {
          setStatus([401, 403].includes(error.response?.status) ? "signin" : "offline");
          setReady(true);
        }
      } finally {
        running.current = null;
      }
      return success;
    })();
    return running.current;
  }, [phone, ensureCartSession, cartRef, save]);

  useEffect(() => { sync(); }, [cart.changeId, sync]);

  useEffect(() => {
    const saveOnExit = () => {
      const snapshot = cartRef.current;
      if (!phone || !snapshot.dirty || !restored.current || !token.current) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/sync`, {
        method: "POST", credentials: "include", keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token.current}` },
        body: JSON.stringify(cartPayload(snapshot)),
      }).catch(() => {});
    };
    const visibility = () => { if (document.visibilityState === "hidden") saveOnExit(); else sync(); };
    window.addEventListener("pagehide", saveOnExit);
    window.addEventListener("online", sync);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      saveOnExit();
      window.removeEventListener("pagehide", saveOnExit);
      window.removeEventListener("online", sync);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [phone, sync, cartRef]);

  return { syncStatus: status, cartReady: ready, retrySync: sync };
}